.Card {
  /* Keeps the dynamic height row on the description slot in the default layout so variable-height content fills the card body. */
  --Card-grid-template-rows: auto auto auto auto auto auto 1fr auto;

  text-decoration: none;
  color: var(--brand-color-text-default);

  padding: var(--brand-Card-padding-block) var(--brand-Card-padding-inline);
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    'image image'
    'leadingVisual leadingVisual'
    'icon icon'
    'tokens tokens'
    'label label'
    'heading heading'
    'description description'
    'action action';
  flex: auto;
  grid-template-rows: var(--Card-grid-template-rows);
  position: relative;
  height: 100%;
  border-radius: var(--brand-borderRadius-medium);
}

@media screen and (min-width: 64rem) {
  .Card {
    --brand-Card-padding-inline: var(--base-size-32);
  }
}

.Card--align-center .Card {
  --brand-Card-padding-block: var(--base-size-64);
  --brand-Card-padding-inline: var(--base-size-40);

  text-align: center;
}

@media screen and (min-width: 64rem) {
  .Card--align-center .Card {
    --brand-Card-padding-inline: var(--base-size-80);
  }
}

.Card--align-center .Card {
  grid-template-columns: 1fr;
  grid-template-areas:
    'image'
    'leadingVisual'
    'icon'
    'tokens'
    'label'
    'heading'
    'description'
    'action';
  justify-items: center;
}

.Card--backgroundColor-default {
  background-color: var(--brand-Card-background-default);
}

.Card--backgroundColor-subtle {
  background-color: var(--brand-color-canvas-subtle);
}

.Card--backgroundColor-none {
  background-color: transparent;
}

.Card--tokensPosition-block-end {
  /* Moves the dynamic height row from tokens back to description because the block-end layout places tokens below the body copy. */
  --Card-grid-template-rows: auto auto auto auto auto 1fr auto auto;

  grid-template-areas:
    'image image'
    'leadingVisual leadingVisual'
    'icon icon'
    'label label'
    'heading heading'
    'description description'
    'tokens tokens'
    'action action';
}

.Card--align-center .Card--tokensPosition-block-end {
  grid-template-areas:
    'image'
    'leadingVisual'
    'icon'
    'label'
    'heading'
    'description'
    'tokens'
    'action';
}

/** Block end image position option */
.Card--imagePos-block-end {
  /* Moves the dynamic height row from action back to description because the image is rendered after the content in this layout. */
  --Card-grid-template-rows: auto auto auto auto auto 1fr auto auto;

  grid-template-areas:
    'leadingVisual leadingVisual'
    'icon icon'
    'tokens tokens'
    'label label'
    'heading heading'
    'description description'
    'action action'
    'image image';
}

.Card--align-center .Card--imagePos-block-end {
  grid-template-areas:
    'leadingVisual'
    'icon'
    'tokens'
    'label'
    'heading'
    'description'
    'action'
    'image';
}

.Card--imagePos-block-end.Card--tokensPosition-block-end {
  /* Moves the dynamic height row from tokens back to description because both the tokens and image are rendered after the body copy here. */
  --Card-grid-template-rows: auto auto auto auto 1fr auto auto auto;

  grid-template-areas:
    'leadingVisual leadingVisual'
    'icon icon'
    'label label'
    'heading heading'
    'description description'
    'tokens tokens'
    'action action'
    'image image';
}

.Card--align-center .Card--imagePos-block-end.Card--tokensPosition-block-end {
  grid-template-areas:
    'leadingVisual'
    'icon'
    'label'
    'heading'
    'description'
    'tokens'
    'action'
    'image';
}

.Card--hasLeadingVisual {
  --brand-Card-padding-block: var(--base-size-64);
  --brand-Card-padding-inline: var(--base-size-40);
}

.Card--imagePos-block-end .Card__image {
  margin-bottom: 0;
  margin-top: var(--base-size-28);
}

.Card--maxWidth {
  max-width: var(--brand-Card-maxWidth);
}

.Card--fullWidth {
  max-width: unset;
}

.Card--variant-minimal {
  padding: 0;
}

.Card--align-center .Card--variant-minimal {
  padding: 0;
}

.Card--ctaVariant-arrow {
  --Card-arrowAction-endInset: var(--base-size-40);
}

.Card:hover {
  text-decoration: none;
}

.Card__link {
  color: currentColor;
  text-decoration: none !important;
}

.Card__link code,
.Card__description code {
  font-size: inherit;
  font-family: var(--brand-fontStack-monospace);
  background-color: var(--brand-color-canvas-subtle);
  padding: 0 var(--base-size-4) var(--base-size-2);
  margin-inline: var(--base-size-2);
  border: var(--brand-borderWidth-thin) solid var(--brand-color-border-default);
  border-radius: var(--brand-borderRadius-small);
  box-shadow: var(--brand-borderWidth-thin) var(--brand-borderWidth-thin) 0 0 var(--brand-color-border-default);
}

.Card__description code {
  padding: var(--base-size-2) var(--base-size-4) 0;
}
.Card__link:has(code) {
  line-height: calc(1lh * 1.4);
}

.Card__description.Card__description:has(code) {
  line-height: calc(1lh * 1.25);
}

.Card--disableAnimation:hover .Card__link {
  text-decoration: underline !important;
}

/* The focus outline will appear around the entire card, not just the title */
.Card__link:focus {
  outline: none;
}

.Card__link::before {
  content: '';
  position: absolute;
  inset: 0;
}

.Card--border {
  border: var(--brand-borderWidth-thin) solid var(--brand-color-border-default);
  border-radius: var(--brand-borderRadius-medium);
}

.Card__outer {
  height: 100%;
}

.Card__outer:has(.Card--variant-minimal) {
  border-radius: var(--brand-borderRadius-small);
}

.Card__outer:focus-within {
  outline: var(--brand-borderWidth-thick) solid var(--brand-color-focus);
  border-radius: var(--brand-borderRadius-medium);
}

.Card__outer:has(.Card--variant-minimal):focus-within {
  outline-offset: var(
    --base-size-12
  ); /* This value is adjusted so that, when combined with the container's border radius, the total border radius equals `var(--brand-borderRadius-medium)` */
}

.Card__image {
  margin-block-end: var(--base-size-28);
  border-radius: var(--brand-borderRadius-medium);
  overflow: hidden;
  grid-area: image;
}

.Card__image--padding-none {
  margin-inline: calc(var(--brand-Card-padding-inline) * -1);
}

.Card__image--position-block-start.Card__image--padding-none {
  margin-block-start: calc(var(--brand-Card-padding-block) * -1);
}

.Card__image--position-block-end.Card__image--padding-none {
  margin-block-end: calc(var(--brand-Card-padding-block) * -1);
}

.Card--border .Card__image--position-block-start.Card__image--padding-none {
  border-end-start-radius: 0;
  border-end-end-radius: 0;
}

.Card--border .Card__image--position-block-end.Card__image--padding-none {
  border-block-start: var(--brand-borderWidth-thin) solid var(--brand-color-border-muted);
  border-start-start-radius: 0;
  border-start-end-radius: 0;
}

.Card--variant-minimal .Card__image--padding-none {
  margin-inline: 0;
}

.Card--variant-minimal .Card__image--position-block-start.Card__image--padding-none {
  margin-block-start: 0;
}

.Card--variant-minimal .Card__image--position-block-end.Card__image--padding-none {
  margin-block-end: 0;
}

.Card__image img,
.Card__image picture,
.Card__image span {
  display: block;
}

.Card__leadingVisual {
  margin-block-end: var(--base-size-36);
  display: flex;
  align-items: center;
  grid-area: leadingVisual;
}

.Card--align-center .Card__leadingVisual {
  justify-content: center;
}

.Card__leadingVisualItem {
  display: block;
  max-width: 100%;
  height: var(--base-size-40);
  width: auto;
}

.Card__icon {
  margin-bottom: var(--base-size-24);
  grid-area: icon;
}

.Card__tokens {
  display: flex;
  flex-wrap: wrap;
  gap: var(--base-size-8);
  grid-area: tokens;
  margin-bottom: var(--base-size-20);
}

.Card--align-center .Card__tokens {
  justify-content: center;
}

.Card__tokens--position-block-end {
  margin-block-start: var(--base-size-20);
  margin-block-end: 0;
}

.Card__label {
  margin-block-end: var(--base-size-20);
  grid-area: label;
  inline-size: fit-content;
  max-inline-size: 100%;
}

.Card__heading {
  margin-block-end: var(--base-size-12);
  grid-area: heading;
}

.Card__description {
  margin-block-end: var(--base-size-24);
  grid-area: description;
}

.Card__action {
  grid-area: action;
  margin-block-start: auto;
  display: inline-flex;
  align-items: center;
  max-inline-size: 100%;
  pointer-events: none;
}

.Card__actionIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.Card__actionLabel {
  display: block;
  min-inline-size: 0;
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
}

.Card:not(.Card--ctaVariant-arrow) .Card__action {
  inline-size: 100%;
  min-inline-size: 0;
}

.Card:not(.Card--ctaVariant-arrow) .Card__action > span:first-child {
  min-inline-size: 0;
}

.Card:not(.Card--ctaVariant-arrow) .Card__actionLabel {
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
  overflow-wrap: anywhere;
}

.Card--align-center:not(.Card--ctaVariant-arrow) .Card__actionLabel {
  text-align: center;
}

.Card__actionLabelClip {
  display: contents;
}

.Card--tokensPosition-block-end .Card__action {
  margin-block-start: var(--base-size-20);
}

.Card__action--arrowOnly {
  --actionArrow-duration: 300ms;

  margin-block-start: var(--base-size-20);
  min-height: var(--base-size-40);
  width: auto;
  min-inline-size: 0;
  max-inline-size: calc(100% - var(--Card-arrowAction-endInset));
  justify-content: flex-start;
  column-gap: var(--base-size-8);
  padding-block: var(--base-size-4);
  padding-inline-start: var(--base-size-12);
  padding-inline-end: var(--base-size-4);
  border: var(--brand-borderWidth-thin) solid var(--brand-color-border-default);
  border-radius: var(--brand-borderRadius-full);
  background-color: var(--brand-Card-background-overlay);
  flex-shrink: 0;
  overflow: hidden;
}

.Card__actionIcon--arrowOnly {
  width: var(--base-size-32);
  min-width: var(--base-size-32);
  height: var(--base-size-32);
  min-height: var(--base-size-32);
  border: var(--brand-borderWidth-thin) solid var(--brand-color-border-default);
  border-radius: var(--brand-borderRadius-full);
  background-color: var(--brand-color-canvas-default);
}

.Card--align-center .Card__action {
  justify-content: center;
}

.Card__action,
.Card__action span {
  color: var(--brand-Link-color-accent);
}

.Card__action,
.Card__action * {
  cursor: inherit;
}

.Card--skew:focus-within {
  outline: var(--brand-borderWidth-thick) solid var(--brand-color-focus);
}

.Card--skew:hover .Card__torch {
  opacity: 0.85;
}

.Card__skew-bounding {
  border-radius: var(--brand-borderRadius-medium);
  position: relative;
  overflow: hidden;
  z-index: 1;
  height: 100%;
  background-color: var(--brand-Card-background-default);
}

.Card__torch {
  width: 650px;
  height: 650px;
  background-color: var(--brand-color-accent-primary);
  filter: blur(180px);
  border-radius: 300px;
  opacity: 0;
  transition: opacity 1.2s cubic-bezier(0.16, 1, 0.3, 1);
  mix-blend-mode: soft-light;
  will-change: transform;
  pointer-events: none;
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: -1;
}

.Card--expandableArrow {
  margin-inline-start: var(--base-size-4);
}

.Card--ctaVariant-arrow .Card--expandableArrow {
  --ExpandableArrow-duration: var(--brand-Card-animation-duration);
  --ExpandableArrow-easing: var(--brand-Card-animation-easing);
}

.Card__actionIcon--arrowOnly .Card--expandableArrow {
  margin-inline-start: 0;
}

.Card--ctaVariant-arrow .Card__description {
  margin-bottom: var(--base-size-12);
}

.Card--ctaVariant-arrow .Card__action {
  min-inline-size: 0;
  justify-self: start;
}

@media (hover: hover) {
  .Card__action--arrowOnly {
    column-gap: 0;
    padding-inline: 0;
    border-color: transparent;
    background-color: transparent;
    transition: border-color var(--actionArrow-duration) ease-out, background-color var(--actionArrow-duration) ease-out,
      column-gap var(--actionArrow-duration) ease-out, padding-inline var(--actionArrow-duration) ease-out;
  }

  .Card__actionLabelClip {
    --actionLabelClip-duration: 400ms;

    display: inline-flex;
    flex: 1 1 auto;
    align-items: center;
    align-self: stretch;
    min-inline-size: 0;
    overflow: hidden;
    max-inline-size: 0;
    background-color: transparent;
    opacity: 0;
    transform: translateX(calc(var(--base-size-12) * -1));
    margin-inline: 0;
    padding-inline: 0;
    transition: max-inline-size var(--actionLabelClip-duration) var(--brand-Card-animation-easing),
      opacity var(--actionLabelClip-duration) ease-out, transform var(--actionLabelClip-duration) ease-out,
      margin-inline var(--actionLabelClip-duration) ease-out, padding-inline var(--actionLabelClip-duration) ease-out;
  }

  .Card--ctaVariant-arrow .Card__actionLabel {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.Card--align-center .Card--ctaVariant-arrow .Card__action {
  justify-self: center;
}

.Card--ctaVariant-arrow:is(:hover, :focus-within) .Card__action--arrowOnly {
  column-gap: var(--base-size-8);
  padding-inline-start: var(--base-size-12);
  padding-inline-end: var(--base-size-4);
  border-color: var(--brand-color-border-default);
  background-color: var(--brand-Card-background-overlay);
}

.Card--ctaVariant-arrow:is(:hover, :focus-within) .Card__actionLabelClip {
  max-inline-size: 100%;
  opacity: 1;
  transform: translateX(0);
  margin-inline-start: calc(var(--base-size-12) * -1);
  margin-inline-end: 0;
  padding-inline-start: var(--base-size-12);
  padding-inline-end: var(--base-size-4);
}

.Card:is(:hover, :focus-within) .Card--expandableArrow {
  transform: translateX(var(--base-size-2));
}

.Card:is(:hover, :focus-within) .Card--expandableArrow path:nth-of-type(2) {
  stroke-dashoffset: 20;
}

.Card--disableAnimation,
.Card--disableAnimation * {
  animation: none;
  transition: none;
}

/* Remove all animations, transitions and smooth scroll for people that prefer not to see them */
@media (prefers-reduced-motion: reduce) {
  .Card,
  .Card::before,
  .Card::after,
  .Card * {
    animation: none !important;
    transition: none !important;
    scroll-behavior: auto !important;
  }
}
